import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { api, fmtMoney, fmtDate } from '../lib/api.js';
import TransactionEditor from '../components/TransactionEditor.jsx';

const TYPES = [
  { value: '',        label: 'All' },
  { value: 'expense', label: 'Expense' },
  { value: 'income',  label: 'Income' },
];

export default function Transactions() {
  const { isOwner } = useOutletContext();
  // tag is a single string like 'batch:4', 'cattle:1', or 'project:2'.
  // Maps onto exactly one of the chicken_batch_id / cattle_id / project_id
  // backend filters when sent.
  const [filters, setFilters] = useState({ type: '', segment: '', tag: '', from: '', to: '' });
  const [sort, setSort] = useState({ field: 'date', dir: 'desc' });
  const [data, setData] = useState(null);
  const [totals, setTotals] = useState(null);
  const [editing, setEditing] = useState(null);    // { type, id }
  const [segments, setSegments] = useState([]);
  const [tagOpts, setTagOpts] = useState({ broiler: [], layer: [], cattle: [], projects: [] });
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/api/segments').then(d => setSegments(d.segments || [])).catch(() => {});
    // Pull the universe of tags so the dropdown can include retired/done items
    Promise.all([
      api.get('/api/chickens?all=1').catch(() => ({ batches: [] })),
      api.get('/api/cattle?all=1').catch(() => ({ cattle: [] })),
      api.get('/api/projects?all=1').catch(() => ({ projects: [] })),
    ]).then(([ch, ca, pr]) => {
      const broiler = (ch.batches || []).filter(b => b.type === 'broiler');
      const layer   = (ch.batches || []).filter(b => b.type === 'layer');
      setTagOpts({
        broiler,
        layer,
        cattle: ca.cattle || [],
        projects: pr.projects || [],
      });
    }).catch(() => {});
  }, []);

  function tagToParam(tag) {
    if (!tag) return null;
    const [kind, id] = tag.split(':');
    if (kind === 'batch')   return ['chicken_batch_id', id];
    if (kind === 'cattle')  return ['cattle_id', id];
    if (kind === 'project') return ['project_id', id];
    return null;
  }

  async function load() {
    const qs = new URLSearchParams();
    if (filters.type)    qs.set('type', filters.type);
    if (filters.segment) qs.set('segment', filters.segment);
    const tagParam = tagToParam(filters.tag);
    if (tagParam) qs.set(tagParam[0], tagParam[1]);
    if (filters.from)    qs.set('from', filters.from);
    if (filters.to)      qs.set('to', filters.to);
    qs.set('sort', sort.field);
    qs.set('dir', sort.dir);
    qs.set('limit', '200');
    try {
      const [list, tot] = await Promise.all([
        api.get(`/api/transactions?${qs}`),
        api.get(`/api/transactions/totals?${qs}`),
      ]);
      setData(list);
      setTotals(tot);
    } catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); }, [filters, sort]);

  function toggleSort(field) {
    setSort(s => s.field === field
      ? { field, dir: s.dir === 'desc' ? 'asc' : 'desc' }
      : { field, dir: field === 'date' || field === 'amount' ? 'desc' : 'asc' });
  }

  if (err) return <div className="text-rust-700 text-sm">{err}</div>;

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-serif text-olive-900">Transactions</h1>
        <div className="text-xs text-ink-700/60">{data ? `${data.total} rows` : '…'}</div>
      </header>

      {totals && isOwner && (
        <div className="grid grid-cols-3 gap-3">
          <Tile label="Income"  value={fmtMoney(totals.income_cents)}  tone="olive" sub={`${totals.income_count} txn${totals.income_count===1?'':'s'}`} />
          <Tile label="Expense" value={fmtMoney(totals.expense_cents)} tone="rust"  sub={`${totals.expense_count} txn${totals.expense_count===1?'':'s'}`} />
          <Tile label="Net"     value={fmtMoney(totals.net_cents)}     tone={totals.net_cents >= 0 ? 'olive' : 'rust'} />
        </div>
      )}

      <div className="card grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div>
          <label className="label">Type</label>
          <select className="input" value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}>
            {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Segment</label>
          <select className="input" value={filters.segment} onChange={e => setFilters(f => ({ ...f, segment: e.target.value }))}>
            <option value="">All</option>
            {segments.filter(s => s.visible_to_me !== false).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="col-span-2 sm:col-span-1">
          <label className="label">Batch / animal / project</label>
          <select className="input" value={filters.tag} onChange={e => setFilters(f => ({ ...f, tag: e.target.value }))}>
            <option value="">All</option>
            {tagOpts.broiler.length > 0 && (
              <optgroup label="Broiler batches">
                {tagOpts.broiler.map(b => <option key={`batch:${b.id}`} value={`batch:${b.id}`}>{b.name}{b.status !== 'active' ? ` · ${b.status}` : ''}</option>)}
              </optgroup>
            )}
            {tagOpts.layer.length > 0 && (
              <optgroup label="Layer flocks">
                {tagOpts.layer.map(b => <option key={`batch:${b.id}`} value={`batch:${b.id}`}>{b.name}{b.status !== 'active' ? ` · ${b.status}` : ''}</option>)}
              </optgroup>
            )}
            {tagOpts.cattle.length > 0 && (
              <optgroup label="Cattle">
                {tagOpts.cattle.map(c => <option key={`cattle:${c.id}`} value={`cattle:${c.id}`}>{c.ear_tag || `#${c.id}`}{c.name ? ` — ${c.name}` : ''}{c.disposition !== 'active' ? ` · ${c.disposition}` : ''}</option>)}
              </optgroup>
            )}
            {tagOpts.projects.length > 0 && (
              <optgroup label="Projects">
                {tagOpts.projects.map(p => <option key={`project:${p.id}`} value={`project:${p.id}`}>{p.name}{p.status !== 'active' ? ` · ${p.status}` : ''}</option>)}
              </optgroup>
            )}
          </select>
        </div>
        <div>
          <label className="label">From</label>
          <input className="input" type="date" value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} />
        </div>
        <div>
          <label className="label">To</label>
          <input className="input" type="date" value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} />
        </div>
      </div>

      {!data ? <div className="text-ink-700/60 italic">Loading…</div> : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-ink-700/60 border-b border-olive-100">
                <SortHeader label="Date"     active={sort} field="date"         onClick={toggleSort} className="text-left" />
                <SortHeader label="Type"     active={sort} field="type"         onClick={toggleSort} className="text-left" />
                <SortHeader label="Counterparty" active={sort} field="counterparty" onClick={toggleSort} className="text-left" />
                <SortHeader label="Segment"  active={sort} field="segment"      onClick={toggleSort} className="text-left" />
                <th className="text-left py-2 px-2">Pmt</th>
                <th className="text-left py-2 px-2">Flags</th>
                <SortHeader label="Amount"   active={sort} field="amount"       onClick={toggleSort} className="text-right" />
                <th className="py-2 px-2 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {data.transactions.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-8 text-ink-700/60 italic">No transactions match the current filters.</td></tr>
              ) : data.transactions.map(t => (
                <tr key={t.key} className="border-b border-olive-100/40 hover:bg-cream-100/40 cursor-pointer" onClick={() => setEditing({ type: t.type, id: t.id })}>
                  <td className="py-1.5 px-2 whitespace-nowrap">{fmtDate(t.date)}</td>
                  <td className="py-1.5 px-2">
                    <span className={`pill ${t.type === 'income' ? 'bg-olive-100 text-olive-900' : 'bg-rust-500/10 text-rust-700'}`}>
                      {t.type}
                    </span>
                  </td>
                  <td className="py-1.5 px-2 truncate max-w-[200px]">{t.counterparty || <span className="italic text-ink-700/40">—</span>}</td>
                  <td className="py-1.5 px-2 text-xs text-ink-700/70">
                    {t.segment_name || (t.segment_id === 'mixed' ? <em>mixed</em> : '—')}
                    {t.subsegment_label && (
                      <div className="text-[10px] text-olive-700/70 mt-0.5">
                        ↳ {t.subsegment_label === 'mixed' ? <em>mixed batches</em> : t.subsegment_label}
                      </div>
                    )}
                  </td>
                  <td className="py-1.5 px-2 text-xs text-ink-700/70">{t.payment_method || '—'}</td>
                  <td className="py-1.5 px-2 text-xs">
                    {t.flag1 ? <span className="pill bg-gold-500/15 text-gold-600 mr-1">no rcpt</span> : null}
                    {t.flag2 ? (
                      t.type === 'expense'
                        ? <span className="pill bg-rust-500/10 text-rust-700 mr-1">reimbursable</span>
                        : <span className="pill bg-gold-500/15 text-gold-600 mr-1">A/R</span>
                    ) : null}
                    {t.bank_transaction_id ? <span className="pill bg-olive-100 text-olive-900" title="Reconciled with bank">✓ bank</span> : null}
                  </td>
                  <td className={`py-1.5 px-2 text-right font-medium whitespace-nowrap ${t.type === 'income' ? 'text-olive-900' : 'text-rust-700'}`}>
                    {t.type === 'income' ? '+' : '−'}{fmtMoney(t.amount_cents)}
                    {t.receipt_total_cents != null && t.receipt_total_cents !== t.amount_cents && (
                      <div className="text-[10px] text-ink-700/50 italic mt-0.5">
                        of {fmtMoney(t.receipt_total_cents)} receipt
                      </div>
                    )}
                  </td>
                  <td className="py-1.5 px-2 text-right text-ink-700/40">›</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-ink-700/50 mt-3">
            Click any row to edit. Owner can delete from the editor.
            {(filters.tag || filters.segment) && (
              <span className="ml-2">
                Filtered amounts show only the line items matching your filter — click a row to see the full receipt.
              </span>
            )}
          </p>
        </div>
      )}

      {editing && (
        <TransactionEditor
          type={editing.type}
          id={editing.id}
          isOwner={isOwner}
          onClose={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SortHeader({ label, field, active, onClick, className = '' }) {
  const isActive = active.field === field;
  return (
    <th className={`py-2 px-2 cursor-pointer select-none ${className}`} onClick={() => onClick(field)}>
      <span className={isActive ? 'text-olive-900' : ''}>
        {label}
        {isActive && <span className="ml-1">{active.dir === 'desc' ? '▼' : '▲'}</span>}
      </span>
    </th>
  );
}

function Tile({ label, value, tone, sub }) {
  const c = tone === 'rust' ? 'text-rust-700' : tone === 'olive' ? 'text-olive-900' : 'text-ink-900';
  return (
    <div className="card text-center">
      <div className="text-xs uppercase tracking-wider text-ink-700/60">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${c}`}>{value}</div>
      {sub && <div className="text-xs text-ink-700/60 mt-1">{sub}</div>}
    </div>
  );
}
